import React, { useState } from 'react';
import type { CurrentUser } from '../types';
import ChangePINModal from '../components/modals/ChangePINModal';
import { authService } from '../lib/auth';

interface ProfilePageProps {
  currentUser: CurrentUser;
  onUpdateUser?: (updates: Partial<CurrentUser>) => void;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ currentUser, onUpdateUser }) => {
  const [showChangePIN, setShowChangePIN] = useState(false);
  const [loading, setLoading] = useState(false);

  const handlePINChange = async (newPin: string) => {
    // Update PIN in database
    const { staffService } = await import('../lib/database');
    await staffService.update(currentUser.id.toString(), { pin: newPin } as any);
    
    // Update local user object if onUpdateUser is provided
    if (onUpdateUser) {
      onUpdateUser({ pin: newPin });
    }
    
    alert('PIN updated successfully!');
  };

  return (
    <div className="p-4 sm:p-8 h-full overflow-y-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">My Profile</h1>

      <div className="max-w-2xl pb-16">
        {/* Profile Information */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Profile Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <p className="text-gray-900">{currentUser.name}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <p className="text-gray-900">{currentUser.email || 'Not set'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <p className="text-gray-900 capitalize">{currentUser.role}</p>
            </div>

            {currentUser.isAdmin && (
              <div className="bg-pink-50 border border-pink-200 rounded-lg p-3">
                <p className="text-pink-800 font-medium">👑 Administrator</p>
                <p className="text-pink-600 text-sm mt-1">You have full access to all features</p>
              </div>
            )}
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Security Settings</h2>

          <div className="space-y-4">
            {/* Lock Screen PIN */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lock Screen PIN</label>
              <p className="text-gray-600 text-sm mb-2">
                4-digit PIN for quick lock screen unlock
              </p>
              <div className="flex items-center space-x-4">
                <span className="font-mono text-gray-900">
                  {currentUser.pin ? '••••' : 'Not set'}
                </span>
                <button 
                  onClick={() => setShowChangePIN(true)}
                  className="text-pink-500 hover:text-pink-600 text-sm font-medium"
                >
                  Change PIN
                </button>
              </div>
            </div>

            {/* Password */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <p className="text-gray-600 text-sm mb-2">
                Change your account password
              </p>
              <button className="text-pink-500 hover:text-pink-600 text-sm font-medium">
                Change Password
              </button>
            </div>
          </div>
        </div>

        {/* Sign Out */}
        <div className="mt-6">
          <button
            onClick={async () => {
              setLoading(true);
              await authService.signOut();
              window.location.reload();
            }}
            disabled={loading}
            className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50"
          >
            {loading ? 'Signing out...' : 'Sign Out'}
          </button>
        </div>
      </div>

      {/* Change PIN Modal */}
      <ChangePINModal
        isOpen={showChangePIN}
        onClose={() => setShowChangePIN(false)}
        currentUser={currentUser}
        onSave={handlePINChange}
      />
    </div>
  );
};

export default ProfilePage;

